import { useEffect, useState } from "react";
import { api } from "./api";

// Rebounding v3 — section rows (interactions, linked skills, stats w/ tiers by position, flags, outcomes, LFs by outcome, constraints, Base Rebound).
let LIB = null, P = null;
export const loadRebounding = () => LIB ? Promise.resolve(LIB) : (P ||= api.reboundingLibrary().then(d => { LIB = d; return d; }).catch(e => { P = null; throw e; }));
export const useRebounding = () => { const [lib, setLib] = useState(LIB); useEffect(() => { if (!lib) loadRebounding().then(setLib).catch(() => {}); }, [lib]); return lib; };

export const rbList = (lib, list) => (lib?.items || []).filter(i => i.list === list && i.status === "active").sort((a, b) => a.order - b.order);
export const rbItem = (lib, id) => (lib?.items || []).find(i => i.id === id && i.status === "active");
export const lfOf = (lib, id) => (lib?.limiting_factors || []).find(f => f.id === id);
export const lfName = (lib, id) => lfOf(lib, id)?.name || id;
export const solOf = (lib, id) => (lib?.solutions || []).find(s => s.id === id);
export const TAG_ROWS = [["Defensive glass", ["RBO-01", "RBO-02", "RBO-03"]], ["Offensive glass", ["RBO-04", "RBO-05"]], ["Any rebound", ["RBO-06", "RBO-07"]]];
export const POSITIONS = [["guards_wings", "Guards / Wings"], ["bigs", "Bigs"]];
export const positionFromArchetypes = (a) => { const p = a?.offense?.primary || ""; return p.startsWith("OFF-GW-") ? "guards_wings" : p.startsWith("OFF-BG-") ? "bigs" : null; };
export const positionLabel = (pos) => POSITIONS.find(([k]) => k === pos)?.[1] || null;

const n = (v) => (v == null || v === "" || Number.isNaN(Number(v)) ? null : Number(v));
const div = (a, b) => (a == null || !b ? null : a / b);
const frac = (v) => (v == null ? null : v > 1 ? v / 100 : v);
const T4 = ["Low", "Average", "Good", "Elite"];
const tierOf = (v, cuts, labels = T4) => (v == null ? null : labels[cuts.reduce((t, c) => (v >= c ? t + 1 : t), 0)]);
export const CUTS = {
  guards_wings: { dreb30: [3, 4.5, 6], drebPct: [0.08, 0.12, 0.16], oreb30: [0.7, 1.3, 2], orebPct: [0.02, 0.04, 0.06] },
  bigs: { dreb30: [5, 7, 9], drebPct: [0.15, 0.20, 0.25], oreb30: [2, 3, 4], orebPct: [0.06, 0.09, 0.12] },
};
export const pppTier = (v) => tierOf(v, [0.95, 1.05, 1.20], ["Struggling", "Average", "Good", "Elite"]);
const GOOD = new Set(["Good", "Elite"]);

// Stats panel from a Season Stats record (SS- ids) + position → per-30 numbers, %s, tiers by position (both rows when unknown), putbacks, flags (RBF-).
export function computeRebounding(stats, position) {
  if (!stats) return null;
  const s = (id) => n(stats[id]);
  const g = s("SS-001"), min = s("SS-002"), oreb = s("SS-010"), dreb = s("SS-011"), orebPct = frac(s("SS-022")), drebPct = frac(s("SS-023"));
  const dreb30 = min && dreb != null ? dreb / min * 30 : null, oreb30 = min && oreb != null ? oreb / min * 30 : null;
  const pbPpp = s("SS-077"), pbFga = s("SS-144"), pbFgm = s("SS-145"), pbPct = div(pbFgm, pbFga);
  const hasSyn = pbPpp != null || pbFga != null;
  const tiersFor = (pos) => { const c = CUTS[pos]; return { dreb30: tierOf(dreb30, c.dreb30), drebPct: tierOf(drebPct, c.drebPct), oreb30: tierOf(oreb30, c.oreb30), orebPct: tierOf(orebPct, c.orebPct) }; };
  const tiers = position ? { [position]: tiersFor(position) } : { guards_wings: tiersFor("guards_wings"), bigs: tiersFor("bigs") };
  const flags = [];
  if (position) {
    const t = tiers[position];
    if (t.dreb30 === "Low" || t.drebPct === "Low") flags.push("RBF-01");
    if (t.oreb30 === "Low" || t.orebPct === "Low") flags.push("RBF-02");
    if ((GOOD.has(t.oreb30) || GOOD.has(t.orebPct)) && pbPct != null && pbPct < 0.5) flags.push("RBF-03");
    if (position === "bigs" && drebPct != null && drebPct < 0.15) flags.push("RBF-04");
  }
  return { games: g, min, oreb, dreb, dreb30, drebPct, oreb30, orebPct, tiers, position, hasSyn, putback: { ppp: pbPpp, pppTier: pppTier(pbPpp), fga: pbFga, fgm: pbFgm, pct: pbPct }, flags };
}
export const fmtPct = (v) => (v == null || Number.isNaN(v) ? "—" : `${(v * 100).toFixed(1)}%`);
export const fmt1 = (v) => (v == null || Number.isNaN(v) ? "—" : Number(v).toFixed(1));
export const fmt2 = (v) => (v == null || Number.isNaN(v) ? "—" : Number(v).toFixed(2));
